import * as rd from '@devexperts/remote-data-ts'
import { useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'

import { useAppDispatch, useAppSelector } from '@/hooks/store'
import { bookingAvailabilitySlice } from '@/store/bookingAvailabilitySlice'
import { cartSlice } from '@/store/cartSlice'
import { licenseSlice } from '@/store/licenseSlice'
import { mapSelectors } from '@/store/mapSlice'
import { getBookingFlowFromSearchParams } from '@/utils/bookingFlow'
import { findMapElementIdForReservation } from '@/utils/mapReservationModelsToQuoteReservations'
import { getPendingCartToken } from '@/utils/pendingCartToken'

export function useRestorePendingCart() {
  const dispatch = useAppDispatch()
  const [searchParams] = useSearchParams()
  const restoreAttempted = useRef(false)

  const licenseRemoteData = useAppSelector(
    licenseSlice.selectors.licenseRemoteData
  )
  const licenseCode = useAppSelector(licenseSlice.selectors.licenseCode)
  const cartReservations = useAppSelector(cartSlice.selectors.cartReservations)
  const isCartPaid = useAppSelector(cartSlice.selectors.isCartPaid)

  useEffect(() => {
    if (!rd.isSuccess(licenseRemoteData) || restoreAttempted.current) {
      return
    }

    const pendingCartToken = getPendingCartToken(licenseCode)
    if (!pendingCartToken || isCartPaid || cartReservations.length > 0) {
      return
    }

    restoreAttempted.current = true

    const restore = async () => {
      try {
        await dispatch(cartSlice.actions.restorePendingCart()).unwrap()

        const bookingFlow = getBookingFlowFromSearchParams(searchParams)
        await dispatch(
          bookingAvailabilitySlice.actions.load({ bookingFlow })
        ).unwrap()

        await dispatch(
          cartSlice.actions.quote({ needsToUpdateCartTotal: true })
        ).unwrap()
      } catch {
        // Token cleared in restorePendingCart on API failure
      }
    }

    void restore()
  }, [
    cartReservations.length,
    dispatch,
    isCartPaid,
    licenseCode,
    licenseRemoteData,
    searchParams,
  ])
}

export function useSyncRestoredCartItems() {
  const dispatch = useAppDispatch()
  const cartReservations = useAppSelector(cartSlice.selectors.cartReservations)
  const cartItems = useAppSelector(cartSlice.selectors.items)
  const mapRemoteData = useAppSelector(mapSelectors.mapRemoteData)
  const licenseCode = useAppSelector(licenseSlice.selectors.licenseCode)

  useEffect(() => {
    if (
      !rd.isSuccess(mapRemoteData) ||
      cartReservations.length === 0 ||
      Object.keys(cartItems).length > 0
    ) {
      return
    }

    if (!getPendingCartToken(licenseCode)) {
      return
    }

    cartReservations.forEach((reservation) => {
      const elementId = findMapElementIdForReservation(
        mapRemoteData.value.elements ?? [],
        reservation
      )

      if (elementId !== null) {
        dispatch(cartSlice.actions.setCartItem({ id: elementId, quantity: 1 }))
      }
    })
  }, [cartItems, cartReservations, dispatch, licenseCode, mapRemoteData])
}
